import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionItem,
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CopyIcon,
  Divider,
  ErrorIcon,
  CheckCircleIcon,
  Text,
} from '@billda/design-system';
import { Auth } from '@billda/permissions-ui';
import { useAuthStore } from '../../../features/auth/stores/authStore';

export const JwtDebugSection: React.FC = () => {
  const token = useAuthStore(state => state.token);
  const [parsedToken, setParsedToken] = useState<any | null>(null);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      const parsed = Auth.parseToken(token);
      setParsedToken(parsed);
    } else {
      setParsedToken(null);
    }
  }, [token]);

  // Decode raw JWT payload claims for debug display and test selectors
  const rawClaims = React.useMemo(() => {
    if (!token) {
      return null;
    }
    try {
      const parts = token.split('.');
      if (parts.length < 2) {
        return null;
      }
      const payloadBase64 = parts[1];
      const normalized = payloadBase64
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(
          payloadBase64.length + ((4 - (payloadBase64.length % 4)) % 4),
          '='
        );
      const json = atob(normalized);
      return JSON.parse(json) as Record<string, any>;
    } catch {
      return null;
    }
  }, [token]);

  useEffect(() => {
    // Update countdown timer
    const expDate = parsedToken?.expiresAt as Date | undefined;
    if (expDate) {
      const updateTimer = () => {
        const now = Date.now();
        const diff = Math.floor((expDate.getTime() - now) / 1000);
        if (diff <= 0) {
          setTimeUntilExpiry('Expired');
          return;
        }
        const days = Math.floor(diff / (24 * 60 * 60));
        const hours = Math.floor((diff % (24 * 60 * 60)) / (60 * 60));
        const minutes = Math.floor((diff % (60 * 60)) / 60);
        const seconds = diff % 60;
        if (days > 0) {
          setTimeUntilExpiry(`${days}d ${hours}h ${minutes}m`);
        } else if (hours > 0) {
          setTimeUntilExpiry(`${hours}h ${minutes}m ${seconds}s`);
        } else if (minutes > 0) {
          setTimeUntilExpiry(`${minutes}m ${seconds}s`);
        } else {
          setTimeUntilExpiry(`${seconds}s`);
        }
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [parsedToken]);

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  if (!token) {
    return (
      <Alert severity="warning" className="mb-2">
        No authentication token found in localStorage
      </Alert>
    );
  }

  if (!parsedToken) {
    return (
      <Alert severity="error" className="mb-2">
        Failed to decode JWT token - token may be malformed
      </Alert>
    );
  }

  const expired = token ? Auth.isTokenExpired(token) : false;

  return (
    <div>
      <Text as="h6" variant="h6" className="mb-2">
        JWT Token Information
      </Text>

      {/* Token Status */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <Chip
            icon={expired ? <ErrorIcon /> : <CheckCircleIcon />}
            label={expired ? 'Expired' : 'Valid'}
            color={expired ? 'error' : 'success'}
            variant="outlined"
          />
          {parsedToken.expiresAt && (
            <Text variant="body2" color="secondary">
              {expired ? 'Expired' : 'Expires in'}:{' '}
              <strong>{timeUntilExpiry}</strong>
            </Text>
          )}
        </div>
      </div>

      {/* Token Payload */}
      <div className="mb-2" data-testid="jwt-payload-accordion">
        <Accordion>
          <AccordionItem value="payload" title="Payload Claims">
            <div className="mb-2" data-testid="jwt-claims-list">
              {Object.entries(rawClaims ?? {}).map(([key, value]) => (
                <div
                  key={key}
                  className="mb-2"
                  data-testid={`jwt-claim-${key}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="min-w-[180px] font-bold">
                      <Text variant="body2" className="font-bold">
                        {key}:
                      </Text>
                    </div>
                    <div className="flex-1">
                      <Text
                        as="div"
                        variant="body2"
                        className="break-words font-mono"
                        data-testid={`jwt-claim-${key}-value`}
                      >
                        {typeof value === 'object' && value !== null
                          ? JSON.stringify(value, null, 2)
                          : String(value)}
                      </Text>
                    </div>
                  </div>
                  <Divider className="mt-2" />
                </div>
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                handleCopy(JSON.stringify(rawClaims ?? {}, null, 2), 'Payload')
              }
            >
              <CopyIcon aria-hidden="true" className="size-4" />
              {copySuccess === 'Payload' ? 'Copied!' : 'Copy All Claims'}
            </Button>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Raw Token */}
      <Accordion>
        <AccordionItem value="raw-token" title="Raw Token">
          <Card className="border border-border-default">
            <CardContent className="p-4">
              <Text
                as="div"
                variant="body2"
                data-testid="raw-token-value"
                className="break-all font-mono"
              >
                {token}
              </Text>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopy(token, 'Token')}
                className="mt-2"
              >
                <CopyIcon aria-hidden="true" className="size-4" />
                {copySuccess === 'Token' ? 'Copied!' : 'Copy Token'}
              </Button>
            </CardContent>
          </Card>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
